// SPDX-License-Identifier: BUSL-1.1; Copyright (c) 2025 Social Connect Labs, Inc.; Licensed under BUSL-1.1 (see LICENSE); Apache-2.0 from 2029-06-11
import {
  fetchAllTreesAndCircuits,
  getCommitmentTree,
  useProtocolStore
} from "./chunk-OTRLGZMA.js";
import {
  BiometricEvents,
  KycEvents,
  OnboardingEvents
} from "./chunk-YHZCD23O.js";

// src/config/defaults.ts
var detectDefaultPlatform = () => {
  if (typeof navigator === "undefined" || !navigator.userAgent) {
    return "unknown";
  }
  const userAgent = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios";
  }
  if (/android/.test(userAgent)) {
    return "android";
  }
  return "web";
};
var defaultConfig = {
  timeouts: { scanMs: 6e4 },
  // in future this can be used to enable/disable experimental features
  features: {},
  platform: detectDefaultPlatform(),
  debug: false
};

// src/config/merge.ts
function mergeConfig(base, override) {
  return {
    ...base,
    ...override,
    timeouts: { ...base.timeouts, ...override.timeouts ?? {} },
    features: { ...base.features, ...override.features ?? {} },
    devConfig: override.devConfig ?? base.devConfig
  };
}

// src/errors/SdkError.ts
var SdkError = class extends Error {
  constructor(message, code, category, retryable = false, options) {
    super(message);
    this.name = "SdkError";
    this.code = code;
    this.category = category;
    this.retryable = retryable;
    if (options?.cause) {
      this.cause = options.cause;
    }
  }
};
function notImplemented(name) {
  return new SdkError(`${name} adapter not provided`, "SELF_ERR_ADAPTER_MISSING", "config", false);
}
function sdkError(message, code, category, retryable = false) {
  return new SdkError(message, code, category, retryable);
}

// src/errors/InitError.ts
var InitError = class extends SdkError {
  constructor(message, options) {
    super(message, "SELF_ERR_INIT", "init", false, options);
    this.name = "InitError";
  }
};

// src/errors/LivenessError.ts
var LivenessError = class extends SdkError {
  constructor(message, options) {
    super(message, "SELF_ERR_LIVENESS", "liveness", false, options);
    this.name = "LivenessError";
  }
};

// src/errors/MrzParseError.ts
var MrzParseError = class extends SdkError {
  constructor(message, options) {
    super(message, "SELF_ERR_MRZ_PARSE", "validation", false, options);
    this.name = "MrzParseError";
  }
};

// src/errors/NfcParseError.ts
var NfcParseError = class extends SdkError {
  constructor(message, options) {
    super(message, "SELF_ERR_NFC_PARSE", "validation", false, options);
    this.name = "NfcParseError";
  }
};

// src/errors/index.ts
var SCANNER_ERROR_CODES = {
  UNAVAILABLE: "SELF_ERR_SCANNER_UNAVAILABLE",
  NFC_NOT_SUPPORTED: "SELF_ERR_NFC_NOT_SUPPORTED",
  INVALID_MODE: "SELF_ERR_SCANNER_MODE"
};

// src/processing/mrz.ts
function calculateCheckDigit(input) {
  const weights = [7, 3, 1];
  let sum = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    let value;
    if (char >= "0" && char <= "9") {
      value = parseInt(char, 10);
    } else if (char >= "A" && char <= "Z") {
      value = char.charCodeAt(0) - "A".charCodeAt(0) + 10;
    } else if (char === "<") {
      value = 0;
    } else {
      throw new MrzParseError(`Invalid character in MRZ: ${char}`);
    }
    sum += value * weights[i % 3];
  }
  return sum % 10;
}
function verifyCheckDigit(field, expectedCheckDigit) {
  if (!/^\d$/.test(expectedCheckDigit)) {
    return false;
  }
  const expected = parseInt(expectedCheckDigit, 10);
  try {
    const calculated = calculateCheckDigit(field);
    return calculated === expected;
  } catch {
    return false;
  }
}
function validateTD3Format(lines) {
  if (lines.length !== 2) {
    return false;
  }
  const TD3_line_2_REGEX = /^([A-Z0-9<]{9})([0-9ILDSOG])([A-Z<]{3})/;
  const isTD3 = TD3_line_2_REGEX.test(lines[1]);
  return isTD3;
}
function validateTD1Format(lines) {
  console.log("validateTD1Format", lines);
  const concatenatedLines = lines[0] + lines[1];
  const TD1_REGEX = /^(?<documentType>[A-Z0-9<]{2})(?<issuingCountry>[A-Z<]{3})(?<documentNumber>[A-Z0-9<]{9})(?<checkDigitDocumentNumber>[0-9]{1})(?<optionalData1>[A-Z0-9<]{15})(?<dateOfBirth>[0-9]{6})(?<checkDigitDateOfBirth>[0-9]{1})(?<sex>[MF<]{1})(?<dateOfExpiry>[0-9]{6})(?<checkDigitDateOfExpiry>[0-9]{1})(?<nationality>[A-Z<]{3})(?<optionalData2>[A-Z0-9<]{7})/;
  const isTD1 = TD1_REGEX.test(concatenatedLines) || lines[0].startsWith("I");
  return isTD1;
}
function extractTD3Info(lines) {
  const line1 = lines[0];
  const line2 = lines[1];
  const documentType = line1.slice(0, 1);
  const issuingCountry = line1.slice(2, 5).replace(/</g, "").replace(/[^A-Z]/g, "");
  const documentNumber = line2.slice(0, 9).replace(/</g, "");
  const rawNat = line2.slice(10, 14);
  let nationality = "";
  for (let i = 0; i <= rawNat.length - 3; i++) {
    const candidate = rawNat.slice(i, i + 3);
    if (/^[A-Z]{3}$/.test(candidate)) {
      nationality = candidate;
      break;
    }
  }
  if (!nationality) {
    nationality = rawNat.slice(0, 3).replace(/[^A-Z]/g, "");
  }
  const dateOfBirth = line2.slice(13, 19);
  const dateOfExpiry = line2.slice(21, 27);
  return {
    documentType,
    issuingCountry,
    documentNumber,
    dateOfBirth,
    dateOfExpiry
  };
}
function extractTD1Info(lines) {
  const line1 = lines[0];
  const line2 = lines[1];
  const concatenatedLines = line1 + line2;
  return {
    documentType: concatenatedLines.slice(0, 2),
    issuingCountry: concatenatedLines.slice(2, 5),
    documentNumber: concatenatedLines.slice(5, 14).replace(/</g, "").trim(),
    dateOfBirth: concatenatedLines.slice(30, 36),
    dateOfExpiry: concatenatedLines.slice(38, 44)
  };
}
function validateTD1CheckDigits(lines) {
  const line1 = lines[0];
  const line2 = lines[1];
  const concatenatedLines = line1 + line2;
  const documentNumber = concatenatedLines.slice(5, 14);
  const documentNumberCheckDigit = concatenatedLines.slice(14, 15);
  const dateOfBirth = concatenatedLines.slice(30, 36);
  const dobCheckDigit = concatenatedLines.slice(36, 37);
  const dateOfExpiry = concatenatedLines.slice(38, 44);
  const expiryCheckDigit = concatenatedLines.slice(44, 45);
  return {
    passportNumberChecksum: verifyCheckDigit(documentNumber, documentNumberCheckDigit),
    dateOfBirthChecksum: verifyCheckDigit(dateOfBirth, dobCheckDigit),
    dateOfExpiryChecksum: verifyCheckDigit(dateOfExpiry, expiryCheckDigit),
    compositeChecksum: true
    // TD1 doesn't have a composite check digit like TD3
  };
}
function validateTD3CheckDigits(lines) {
  const line2 = lines[1];
  const passportNumber = line2.slice(0, 9);
  const passportCheckDigit = line2.slice(9, 10);
  const dateOfBirth = line2.slice(13, 19);
  const dobCheckDigit = line2.slice(19, 20);
  const dateOfExpiry = line2.slice(21, 27);
  const expiryCheckDigit = line2.slice(27, 28);
  const compositeField = line2.slice(0, 10) + line2.slice(13, 20) + line2.slice(21, 28) + line2.slice(28, 43);
  const compositeCheckDigit = line2.slice(43, 44);
  return {
    passportNumberChecksum: verifyCheckDigit(passportNumber, passportCheckDigit),
    dateOfBirthChecksum: verifyCheckDigit(dateOfBirth, dobCheckDigit),
    dateOfExpiryChecksum: verifyCheckDigit(dateOfExpiry, expiryCheckDigit),
    compositeChecksum: verifyCheckDigit(compositeField, compositeCheckDigit)
  };
}
function checkScannedInfo(passportNumber, dateOfBirth, dateOfExpiry) {
  if (passportNumber.length > 9) {
    return false;
  }
  if (dateOfBirth.length !== 6) {
    return false;
  }
  if (dateOfExpiry.length !== 6) {
    return false;
  }
  return true;
}
function extractMRZInfo(mrzString) {
  if (!mrzString || typeof mrzString !== "string") {
    throw new MrzParseError("MRZ string is required and must be a string");
  }
  const lines = mrzString.trim().split("\n").map((line) => line.trim());
  const isValidTD3 = validateTD3Format(lines);
  const isValidTD1 = validateTD1Format(lines);
  if (!isValidTD3 && !isValidTD1) {
    throw new MrzParseError(
      `Invalid MRZ format: Expected TD3 or TD1 format, got ${lines.length} lines with lengths [${lines.map((l) => l.length).join(", ")}]`
    );
  }
  let info;
  let checksums;
  let validation;
  if (isValidTD3) {
    info = extractTD3Info(lines);
    checksums = validateTD3CheckDigits(lines);
    validation = {
      format: isValidTD3,
      ...checksums,
      overall: isValidTD3 && Object.values(checksums).every(Boolean)
    };
  } else {
    info = extractTD1Info(lines);
    checksums = validateTD1CheckDigits(lines);
    validation = {
      format: isValidTD1,
      ...checksums,
      overall: isValidTD1 && Object.values(checksums).every(Boolean)
    };
  }
  return {
    ...info,
    validation
  };
}
function extractNameFromMRZ(mrzString) {
  if (!mrzString || typeof mrzString !== "string") {
    return null;
  }
  let lines = mrzString.trim().split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length === 1) {
    const mrzLength = lines[0].length;
    if (mrzLength === 90 && /^[IAC][<A-Z]/.test(lines[0])) {
      lines = [lines[0].slice(0, 30), lines[0].slice(30, 60), lines[0].slice(60, 90)];
    } else if (mrzLength === 88 || mrzLength === 90) {
      const lineLength = mrzLength === 88 ? 44 : 45;
      lines = [lines[0].slice(0, lineLength), lines[0].slice(lineLength)];
    }
  }
  if (lines.length === 0) {
    return null;
  }
  if (lines.length === 2) {
    const line1 = lines[0];
    const nameMatch = line1.match(/^[IPO]<[A-Z]{3}(.+)$/);
    if (nameMatch) {
      const namePart = nameMatch[1];
      const parts = namePart.split("<<").filter(Boolean);
      if (parts.length >= 2) {
        const lastName = parts[0].replace(/</g, " ").trim();
        const firstName = parts[1].replace(/</g, " ").trim();
        return { firstName, lastName };
      } else if (parts.length === 1) {
        const name = parts[0].replace(/</g, " ").trim();
        return { firstName: "", lastName: name };
      }
    }
  }
  if (lines.length === 3) {
    const line3 = lines[2];
    const parts = line3.split("<<").filter(Boolean);
    if (parts.length >= 2) {
      const lastName = parts[0].replace(/</g, " ").trim();
      const firstName = parts[1].replace(/</g, " ").trim();
      return { firstName, lastName };
    } else if (parts.length === 1) {
      const name = parts[0].replace(/</g, " ").trim();
      return { firstName: "", lastName: name };
    }
  }
  return null;
}
var parseMRZDateComponents = (yymmdd) => {
  if (yymmdd.length !== 6) {
    return null;
  }
  return {
    year: parseInt(yymmdd.slice(0, 2)),
    month: parseInt(yymmdd.slice(2, 4)) - 1,
    day: parseInt(yymmdd.slice(4, 6))
  };
};
function formatDateToYYMMDD(inputDate) {
  if (!inputDate || typeof inputDate !== "string") {
    throw new MrzParseError("Date string is required");
  }
  const isoMatch = inputDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return year.slice(2) + month + day;
  }
  const dateMatch = inputDate.match(/^(\d{2,4})[-/]?(\d{2})[-/]?(\d{2})/);
  if (dateMatch) {
    let [, year] = dateMatch;
    const [, , month, day] = dateMatch;
    if (year.length === 2) {
      const yearNum = parseInt(year, 10);
      year = yearNum <= 30 ? `20${year}` : `19${year}`;
    }
    return year.slice(2) + month + day;
  }
  throw new MrzParseError(`Invalid date format: ${inputDate}. Expected ISO format (YYYY-MM-DD) or similar.`);
}
var parseMRZBirthDate = (yymmdd) => {
  const components = parseMRZDateComponents(yymmdd);
  if (!components) return /* @__PURE__ */ new Date();
  const { year, month, day } = components;
  const fullYear = year + (year > 30 ? 1900 : 2e3);
  return new Date(fullYear, month, day);
};
var parseMRZExpiryDate = (yymmdd) => {
  const components = parseMRZDateComponents(yymmdd);
  if (!components) return /* @__PURE__ */ new Date();
  const { year, month, day } = components;
  const fullYear = 2e3 + year;
  return new Date(fullYear, month, day);
};

// src/analytics/onboardingFunnel.ts
var currentAttempt = null;
function uuid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
function ensureAttempt(selfClient) {
  if (!currentAttempt) {
    currentAttempt = {
      id: uuid(),
      initialBranch: "pending",
      currentBranch: "pending",
      startedAt: Date.now(),
      firedSteps: /* @__PURE__ */ new Set([OnboardingEvents.STARTED]),
      retryCounts: {},
      isMock: false
    };
    selfClient.trackEvent(OnboardingEvents.STARTED, baseProperties(currentAttempt));
  }
  return currentAttempt;
}
function durationSeconds(from) {
  return parseFloat(((Date.now() - from) / 1e3).toFixed(2));
}
function baseProperties(attempt) {
  return {
    attempt_id: attempt.id,
    initial_branch: attempt.initialBranch,
    current_branch: attempt.currentBranch
  };
}
function captureBranch(attempt, branch) {
  if (attempt.initialBranch === "pending" && branch !== "pending") {
    attempt.initialBranch = branch;
  }
  attempt.currentBranch = branch;
}
function endOnboardingAttempt(selfClient, outcome, properties) {
  const attempt = currentAttempt;
  if (!attempt) return;
  currentAttempt = null;
  if (attempt.isMock) return;
  selfClient.trackEvent(OnboardingEvents.ENDED, {
    ...properties,
    ...baseProperties(attempt),
    outcome,
    duration_seconds: durationSeconds(attempt.startedAt),
    country_code: attempt.countryCode,
    document_type: attempt.documentType,
    used_fallback: attempt.initialBranch !== attempt.currentBranch
  });
}
function completeOnboardingAttempt(selfClient, properties) {
  endOnboardingAttempt(selfClient, "completed", properties);
}
function recoverOnboardingAttempt(selfClient, properties) {
  endOnboardingAttempt(selfClient, "recovered", properties);
}
function moveOnboardingAttemptToRecovery(selfClient, properties) {
  endOnboardingAttempt(selfClient, "moved_to_recovery", { stage: "validating_document", ...properties });
}
function failOnboardingAttempt(selfClient, stage, reason, properties) {
  endOnboardingAttempt(selfClient, "failed", { ...properties, stage, reason });
}
function markCurrentAttemptAsMock(_selfClient) {
  if (currentAttempt) {
    currentAttempt.isMock = true;
    return;
  }
  currentAttempt = {
    id: uuid(),
    initialBranch: "pending",
    currentBranch: "pending",
    startedAt: Date.now(),
    firedSteps: /* @__PURE__ */ new Set([OnboardingEvents.STARTED]),
    retryCounts: {},
    isMock: true
  };
}
function resolveOnboardingBranch(documentType) {
  switch (documentType) {
    case "p":
    case "passport":
      return "biometric_passport";
    case "i":
    case "id_card":
      return "biometric_id";
    case "a":
    case "aadhaar":
      return "aadhaar";
    case "kyc":
      return "kyc";
    default:
      return "kyc";
  }
}
function setOnboardingBranch(branch) {
  if (!currentAttempt) return;
  currentAttempt.currentBranch = branch;
}
function trackBranchEvent(selfClient, event, properties) {
  if (!currentAttempt) return;
  selfClient.trackEvent(event, {
    ...properties,
    ...baseProperties(currentAttempt)
  });
}
function trackKycVerdict(selfClient, properties) {
  if (currentAttempt?.isMock) return;
  selfClient.trackEvent(KycEvents.VERIFICATION_RESOLVED, {
    ...properties,
    ...currentAttempt ? baseProperties(currentAttempt) : {}
  });
}
function incrementAttemptRetryCount(key) {
  if (!currentAttempt) return 0;
  currentAttempt.retryCounts[key] = (currentAttempt.retryCounts[key] ?? 0) + 1;
  return currentAttempt.retryCounts[key];
}
function trackOnboardingRetry(selfClient, stage, reason, properties) {
  const attempt = ensureAttempt(selfClient);
  attempt.retryCounts[stage] = (attempt.retryCounts[stage] ?? 0) + 1;
  if (attempt.isMock) return;
  selfClient.trackEvent(OnboardingEvents.STEP_RETRIED, {
    ...properties,
    ...baseProperties(attempt),
    stage,
    reason,
    attempt_count: attempt.retryCounts[stage]
  });
}
function trackOnboardingStep(selfClient, event, properties) {
  const attempt = ensureAttempt(selfClient);
  if (properties && typeof properties.branch === "string") {
    captureBranch(attempt, properties.branch);
  }
  if (properties && typeof properties.country_code === "string") {
    attempt.countryCode = properties.country_code;
  }
  if (properties && typeof properties.document_type === "string") {
    attempt.documentType = properties.document_type;
  }
  if (attempt.firedSteps.has(event)) return;
  attempt.firedSteps.add(event);
  if (attempt.isMock) return;
  const { branch: _discardedBranch, ...stampableProperties } = properties ?? {};
  selfClient.trackEvent(event, {
    ...stampableProperties,
    ...baseProperties(attempt)
  });
}

// src/documents/utils.ts
import {
  brutforceSignatureAlgorithmDsc,
  calculateContentHash,
  inferDocumentCategory,
  isAadhaarDocument,
  isKycDocument,
  isMRZDocument,
  parseCertificateSimple
} from "@selfxyz/common";
async function clearPassportData(selfClient) {
  const catalog = await selfClient.loadDocumentCatalog();
  for (const doc of catalog.documents) {
    try {
      await selfClient.deleteDocument(doc.id);
    } catch {
      console.log(`Document ${doc.id} not found or already cleared`);
    }
  }
  await selfClient.saveDocumentCatalog({ documents: [] });
}
async function extractNameFromDocument(selfClient, documentId) {
  try {
    const document = await selfClient.loadDocumentById(documentId);
    if (!document) {
      return null;
    }
    if (isAadhaarDocument(document)) {
      const name = document.extractedFields?.name;
      if (name && typeof name === "string") {
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
          const firstName = parts[0];
          const lastName = parts.slice(1).join(" ");
          return { firstName, lastName };
        } else if (parts.length === 1) {
          return { firstName: parts[0], lastName: "" };
        }
      }
      return null;
    }
    if (isMRZDocument(document)) {
      return extractNameFromMRZ(document.mrz);
    }
    return null;
  } catch (error) {
    console.error("Error extracting name from document:", error);
    return null;
  }
}
var getAllDocuments = async (selfClient) => {
  const catalog = await selfClient.loadDocumentCatalog();
  const allDocs = {};
  for (const metadata of catalog.documents) {
    const data = await selfClient.loadDocumentById(metadata.id);
    if (data) {
      allDocs[metadata.id] = { data, metadata };
    }
  }
  return allDocs;
};
var hasAnyValidRegisteredDocument = async (client) => {
  console.log("Checking if there are any valid registered documents");
  try {
    const catalog = await client.loadDocumentCatalog();
    return catalog.documents.some((doc) => doc.isRegistered === true);
  } catch (error) {
    console.error("Error loading document catalog:", error);
    return false;
  }
};
var loadSelectedDocument = async (selfClient) => {
  const catalog = await selfClient.loadDocumentCatalog();
  console.log("Catalog loaded");
  if (!catalog.selectedDocumentId) {
    console.log("No selectedDocumentId found");
    if (catalog.documents.length > 0) {
      console.log("Using first document as fallback");
      catalog.selectedDocumentId = catalog.documents[0].id;
      await selfClient.saveDocumentCatalog(catalog);
    } else {
      console.log("No documents in catalog, returning null");
      return null;
    }
  }
  const metadata = catalog.documents.find((d) => d.id === catalog.selectedDocumentId);
  if (!metadata) {
    console.log("Metadata not found for selectedDocumentId:", catalog.selectedDocumentId);
    return null;
  }
  const data = await selfClient.loadDocumentById(catalog.selectedDocumentId);
  if (!data) {
    console.log("Document data not found for id:", catalog.selectedDocumentId);
    return null;
  }
  console.log("Successfully loaded document:", metadata.documentType);
  return { data, metadata };
};
async function markCurrentDocumentAsRegistered(selfClient) {
  const catalog = await selfClient.loadDocumentCatalog();
  if (catalog.selectedDocumentId) {
    await updateDocumentRegistrationState(selfClient, catalog.selectedDocumentId, true);
  } else {
    console.warn("No selected document to mark as registered");
  }
}
async function reStorePassportDataWithRightCSCA(selfClient, passportData, csca) {
  if (passportData.documentCategory === "aadhaar" || passportData.documentCategory === "kyc") {
    return;
  }
  const cscaInCurrentPassporData = passportData.passportMetadata?.csca;
  if (!(csca === cscaInCurrentPassporData)) {
    const cscaParsed = parseCertificateSimple(csca);
    const dscCertData = brutforceSignatureAlgorithmDsc(passportData.dsc_parsed, cscaParsed);
    if (passportData.passportMetadata && dscCertData && cscaParsed.publicKeyDetails) {
      passportData.passportMetadata.csca = csca;
      passportData.passportMetadata.cscaFound = true;
      passportData.passportMetadata.cscaHashFunction = dscCertData.hashAlgorithm;
      passportData.passportMetadata.cscaSignatureAlgorithm = dscCertData.signatureAlgorithm;
      passportData.passportMetadata.cscaSaltLength = dscCertData.saltLength;
      const cscaCurveOrExponent = cscaParsed.signatureAlgorithm === "rsapss" || cscaParsed.signatureAlgorithm === "rsa" ? cscaParsed.publicKeyDetails.exponent : cscaParsed.publicKeyDetails.curve;
      passportData.passportMetadata.cscaCurveOrExponent = cscaCurveOrExponent;
      passportData.passportMetadata.cscaSignatureAlgorithmBits = parseInt(cscaParsed.publicKeyDetails.bits, 10);
      passportData.csca_parsed = cscaParsed;
      await storePassportData(selfClient, passportData);
    }
  }
}
async function storeDocumentWithDeduplication(selfClient, passportData) {
  const contentHash = calculateContentHash(passportData);
  const catalog = await selfClient.loadDocumentCatalog();
  const existing = catalog.documents.find((d) => d.id === contentHash);
  if (existing) {
    console.log("Document with same content exists, updating stored data");
    await selfClient.saveDocument(contentHash, passportData);
    catalog.selectedDocumentId = contentHash;
    await selfClient.saveDocumentCatalog(catalog);
    return contentHash;
  }
  await selfClient.saveDocument(contentHash, passportData);
  const docType = passportData.documentType;
  const documentCategory = passportData.documentCategory || inferDocumentCategory(docType);
  const metadata = {
    id: contentHash,
    documentType: docType,
    documentCategory,
    data: isMRZDocument(passportData) ? passportData.mrz : isKycDocument(passportData) ? passportData.serializedApplicantInfo : passportData.qrData || "",
    mock: passportData.mock || false,
    isRegistered: false,
    hasExpirationDate: documentCategory === "id_card" || documentCategory === "passport",
    ...isKycDocument(passportData) ? { idType: passportData.documentType } : {}
  };
  catalog.documents.push(metadata);
  catalog.selectedDocumentId = contentHash;
  await selfClient.saveDocumentCatalog(catalog);
  return contentHash;
}
async function storePassportData(selfClient, passportData) {
  await storeDocumentWithDeduplication(selfClient, passportData);
}
async function updateDocumentRegistrationState(selfClient, documentId, isRegistered) {
  const catalog = await selfClient.loadDocumentCatalog();
  const documentIndex = catalog.documents.findIndex((d) => d.id === documentId);
  if (documentIndex !== -1) {
    catalog.documents[documentIndex].isRegistered = isRegistered;
    if (isRegistered) {
      catalog.documents[documentIndex].registeredAt = Date.now();
    } else {
      catalog.documents[documentIndex].registeredAt = void 0;
    }
    await selfClient.saveDocumentCatalog(catalog);
    console.log(`Updated registration state for document ${documentId}: ${isRegistered}`);
  } else {
    console.warn(`Document ${documentId} not found in catalog`);
  }
}

// src/types/events.ts
var SdkEvents = /* @__PURE__ */ ((SdkEvents2) => {
  SdkEvents2["ERROR"] = "ERROR";
  SdkEvents2["PROGRESS"] = "PROGRESS";
  SdkEvents2["PROVING_AADHAAR_UPLOAD_SUCCESS"] = "PROVING_AADHAAR_UPLOAD_SUCCESS";
  SdkEvents2["PROVING_AADHAAR_UPLOAD_FAILURE"] = "PROVING_AADHAAR_UPLOAD_FAILURE";
  SdkEvents2["PROVING_PASSPORT_DATA_NOT_FOUND"] = "PROVING_PASSPORT_DATA_NOT_FOUND";
  SdkEvents2["PROVING_ACCOUNT_VERIFIED_SUCCESS"] = "PROVING_ACCOUNT_VERIFIED_SUCCESS";
  SdkEvents2["PROVING_REGISTER_ERROR_OR_FAILURE"] = "PROVING_REGISTER_ERROR_OR_FAILURE";
  SdkEvents2["PROVING_PASSPORT_NOT_SUPPORTED"] = "PROVING_PASSPORT_NOT_SUPPORTED";
  SdkEvents2["PROVING_ACCOUNT_RECOVERY_REQUIRED"] = "PROVING_ACCOUNT_RECOVERY_REQUIRED";
  SdkEvents2["DOCUMENT_COUNTRY_SELECTED"] = "DOCUMENT_COUNTRY_SELECTED";
  SdkEvents2["DOCUMENT_TYPE_SELECTED"] = "DOCUMENT_TYPE_SELECTED";
  SdkEvents2["PROVING_BEGIN_GENERATION"] = "PROVING_BEGIN_GENERATION";
  SdkEvents2["PROOF_EVENT"] = "PROOF_EVENT";
  SdkEvents2["NFC_EVENT"] = "NFC_EVENT";
  SdkEvents2["DOCUMENT_MRZ_READ_SUCCESS"] = "DOCUMENT_MRZ_READ_SUCCESS";
  SdkEvents2["DOCUMENT_MRZ_READ_FAILURE"] = "DOCUMENT_MRZ_READ_FAILURE";
  SdkEvents2["DOCUMENT_NFC_SCAN_SUCCESS"] = "DOCUMENT_NFC_SCAN_SUCCESS";
  SdkEvents2["DOCUMENT_OWNERSHIP_CONFIRMED"] = "DOCUMENT_OWNERSHIP_CONFIRMED";
  SdkEvents2["LOGO_CONFIRMED"] = "LOGO_CONFIRMED";
  SdkEvents2["LOGO_NOT_FOUND"] = "LOGO_NOT_FOUND";
  SdkEvents2["VERIFICATION_COMPLETE"] = "VERIFICATION_COMPLETE";
  return SdkEvents2;
})(SdkEvents || {});

// src/proving/provingMachine.ts
import forge from "node-forge";
import socketIo from "socket.io-client";
import { v4 } from "uuid";
import { createActor, createMachine } from "xstate";
import { create } from "zustand";
import {
  getCircuitNameFromPassportData,
  getSKIPEM,
  getSolidityPackedUserContextData,
  initPassportDataParsing
} from "@selfxyz/common/utils";
import { checkPCR0Mapping, validatePKIToken } from "@selfxyz/common/utils/attest";
import {
  generateTEEInputsDiscloseStateless,
  generateTEEInputsDSC,
  generateTEEInputsRegister
} from "@selfxyz/common/utils/circuits/registerInputs";
import {
  checkDocumentSupported,
  checkIfPassportDscIsInTree,
  isDocumentNullified,
  isUserRegistered,
  isUserRegisteredWithAlternativeCSCA
} from "@selfxyz/common/utils/passports/validate";
import {
  clientKey,
  clientPublicKeyHex,
  ec,
  encryptAES256GCM,
  getPayload,
  getWSDbRelayerUrl
} from "@selfxyz/common/utils/proving";

// src/proving/internal/statusHandlers.ts
function handleStatusCode(data, circuitType) {
  const result = {
    shouldDisconnect: false,
    analytics: []
  };
  if (data.status === 5 && data.error_code === "REGISTERED_COMMITMENT") {
    return {
      shouldDisconnect: true,
      actorEvent: { type: "PROVE_ALREADY_REGISTERED" },
      analytics: [
        {
          event: "SOCKETIO_PROOF_FAILURE",
          data: {
            error_code: data.error_code,
            reason: data.reason
          }
        }
      ]
    };
  }
  if (data.status === 3 || data.status === 5) {
    result.shouldDisconnect = true;
    result.stateUpdate = {
      error_code: data.error_code,
      reason: data.reason,
      socketConnection: null
    };
    result.actorEvent = { type: "PROVE_FAILURE" };
    result.analytics = [
      {
        event: "SOCKETIO_PROOF_FAILURE",
        data: {
          error_code: data.error_code,
          reason: data.reason
        }
      }
    ];
    return result;
  }
  if (data.status === 4) {
    result.shouldDisconnect = true;
    result.stateUpdate = {
      socketConnection: null
    };
    result.actorEvent = { type: "PROVE_SUCCESS" };
    result.analytics = [
      {
        event: "SOCKETIO_PROOF_SUCCESS"
      }
    ];
    if (circuitType === "register") {
      result.analytics.push({
        event: "REGISTER_COMPLETED"
      });
    }
    return result;
  }
  return result;
}
function parseStatusMessage(message) {
  if (typeof message === "string") {
    try {
      return JSON.parse(message);
    } catch {
      throw new Error("Invalid JSON message received");
    }
  }
  if (typeof message === "object" && message !== null) {
    return message;
  }
  throw new Error("Invalid message format");
}

// src/proving/provingMachine.ts
var getMappingKey = (circuitType, documentCategory) => {
  if (circuitType === "disclose") {
    if (documentCategory === "passport") return "DISCLOSE";
    if (documentCategory === "id_card") return "DISCLOSE_ID";
    if (documentCategory === "aadhaar") return "DISCLOSE_AADHAAR";
    if (documentCategory === "kyc") return "DISCLOSE_KYC";
    throw new Error(`Unsupported document category for disclose: ${documentCategory}`);
  }
  if (circuitType === "register") {
    if (documentCategory === "passport") return "REGISTER";
    if (documentCategory === "id_card") return "REGISTER_ID";
    if (documentCategory === "aadhaar") return "REGISTER_AADHAAR";
    if (documentCategory === "kyc") return "REGISTER_KYC";
    throw new Error(`Unsupported document category for register: ${documentCategory}`);
  }
  return documentCategory === "passport" ? "DSC" : "DSC_ID";
};
var resolveWebSocketUrl = (selfClient, circuitType, passportData, circuitName) => {
  const { documentCategory } = passportData;
  const circuitsMapping = selfClient.getProtocolState()[documentCategory].circuits_dns_mapping;
  const mappingKey = getMappingKey(circuitType, documentCategory);
  return circuitsMapping?.[mappingKey]?.[circuitName];
};
var _generateCircuitInputs = async (selfClient, circuitType, secret, passportData, env, selfApp) => {
  const document = passportData.documentCategory;
  const protocolStore = selfClient.getProtocolState();
  let inputs, circuitName, endpointType, endpoint, circuitTypeWithDocumentExtension;
  switch (circuitType) {
    case "register":
      ({ inputs, circuitName, endpointType, endpoint } = await generateTEEInputsRegister(
        secret,
        passportData,
        document === "aadhaar" || document === "kyc" ? protocolStore[document].public_keys : protocolStore[document].dsc_tree,
        env
      ));
      circuitTypeWithDocumentExtension = `${circuitType}${document === "passport" ? "" : "_id"}`;
      break;
    case "dsc":
      if (document === "aadhaar") {
        throw new Error("DSC circuit type is not supported for Aadhaar documents");
      }
      if (document === "kyc") {
        throw new Error("DSC circuit type is not supported for KYC documents");
      }
      ({ inputs, circuitName, endpointType, endpoint } = generateTEEInputsDSC(
        passportData,
        protocolStore[document].csca_tree,
        env
      ));
      circuitTypeWithDocumentExtension = `${circuitType}${document === "passport" ? "" : "_id"}`;
      break;
    case "disclose": {
      if (!selfApp) {
        throw new Error("SelfApp context not initialized");
      }
      ({ inputs, circuitName, endpointType, endpoint } = generateTEEInputsDiscloseStateless(
        secret,
        passportData,
        selfApp,
        (doc, tree) => {
          const docStore = doc === "passport" ? protocolStore.passport : doc === "aadhaar" ? protocolStore.aadhaar : doc === "kyc" ? protocolStore.kyc : protocolStore.id_card;
          switch (tree) {
            case "ofac":
              return docStore.ofac_trees;
            case "commitment":
              if (!docStore.commitment_tree) {
                throw new Error("Commitment tree not loaded");
              }
              return docStore.commitment_tree;
            default:
              throw new Error("Unknown tree type");
          }
        }
      ));
      circuitTypeWithDocumentExtension = `disclose`;
      break;
    }
    default:
      throw new Error("Invalid circuit type:" + circuitType);
  }
  return {
    inputs,
    circuitName,
    endpointType,
    endpoint,
    circuitTypeWithDocumentExtension
  };
};
var JSONRPC_VERSION = "2.0";
var SUBMIT_METHOD = "openpassport_submit_request";
var SUBMIT_ID = 2;
var _encryptPayload = (payload, sharedKey) => {
  const forgeKey = forge.util.createBuffer(sharedKey.toString("binary"));
  return encryptAES256GCM(JSON.stringify(payload), forgeKey);
};
var _buildSubmitRequest = (uuid2, encryptedPayload) => {
  return {
    jsonrpc: JSONRPC_VERSION,
    method: SUBMIT_METHOD,
    id: SUBMIT_ID,
    params: {
      uuid: uuid2,
      ...encryptedPayload
    }
  };
};
var getPlatform = (selfClient) => selfClient?.config?.platform ?? "unknown";
var provingMachine = createMachine({
  id: "proving",
  initial: "idle",
  states: {
    idle: {
      on: {
        PARSE_ID_DOCUMENT: "parsing_id_document",
        FETCH_DATA: "fetching_data",
        ERROR: "error",
        PASSPORT_DATA_NOT_FOUND: "passport_data_not_found"
      }
    },
    parsing_id_document: {
      on: {
        PARSE_SUCCESS: "fetching_data",
        PARSE_ERROR: "error"
      }
    },
    fetching_data: {
      on: {
        FETCH_SUCCESS: "validating_document",
        FETCH_ERROR: "error"
      }
    },
    validating_document: {
      on: {
        VALIDATION_SUCCESS: "init_tee_connexion",
        VALIDATION_ERROR: "error",
        ALREADY_REGISTERED: "completed",
        PASSPORT_NOT_SUPPORTED: "passport_not_supported",
        ACCOUNT_RECOVERY_CHOICE: "account_recovery_choice",
        PASSPORT_DATA_NOT_FOUND: "passport_data_not_found"
      }
    },
    init_tee_connexion: {
      on: {
        CONNECT_SUCCESS: "ready_to_prove",
        CONNECT_ERROR: "error"
      }
    },
    ready_to_prove: {
      on: {
        START_PROVING: "proving",
        PROVE_ERROR: "error"
      }
    },
    proving: {
      on: {
        PROVE_SUCCESS: "post_proving",
        PROVE_ERROR: "error",
        PROVE_FAILURE: "failure",
        PROVE_ALREADY_REGISTERED: "account_recovery_choice"
      }
    },
    post_proving: {
      on: {
        SWITCH_TO_REGISTER: "fetching_data",
        COMPLETED: "completed"
      }
    },
    completed: {
      type: "final"
    },
    error: {
      type: "final"
    },
    passport_not_supported: {
      type: "final"
    },
    account_recovery_choice: {
      type: "final"
    },
    passport_data_not_found: {
      type: "final"
    },
    failure: {
      type: "final"
    }
  }
});
var getPostVerificationRoute = () => {
  return "AccountVerifiedSuccess";
};
var useProvingStore = create((set, get) => {
  let actor = null;
  function resetProvingState(partial) {
    set({
      currentState: "idle",
      attestation: null,
      serverPublicKey: null,
      sharedKey: null,
      wsConnection: null,
      wsHandlers: null,
      wsReconnectAttempts: 0,
      socketConnection: null,
      uuid: null,
      userConfirmed: false,
      passportData: null,
      secret: null,
      circuitType: null,
      didNewRegistrationProof: false,
      isMock: false,
      endpointType: null,
      env: null,
      error_code: null,
      reason: null,
      ...partial
    });
  }
  function setupActorSubscriptions(newActor, selfClient) {
    let lastTransition = Date.now();
    let lastEvent = { type: "init" };
    const emitVerificationComplete = (success, error) => {
      const selfApp = selfClient.getSelfAppState().selfApp;
      const provingState = get();
      selfClient.emit("VERIFICATION_COMPLETE" /* VERIFICATION_COMPLETE */, {
        success,
        userId: selfApp?.userId,
        verificationId: provingState.uuid ?? void 0,
        error
      });
    };
    newActor.on("*", (event) => {
      lastEvent = event;
    });
    newActor.subscribe((state) => {
      const now = Date.now();
      const context = createProofContext(selfClient, "stateTransition", {
        currentState: String(state.value)
      });
      selfClient.emit("PROOF_EVENT" /* PROOF_EVENT */, {
        context,
        level: "info",
        event: `state transition: ${state.value}`,
        details: {
          event: lastEvent.type,
          duration_ms: now - lastTransition
        }
      });
      lastTransition = now;
      set({ currentState: state.value });
      if (state.value === "parsing_id_document") {
        get().parseIDDocument(selfClient);
      }
      if (state.value === "fetching_data") {
        get().startFetchingData(selfClient);
      }
      if (state.value === "validating_document") {
        get().validatingDocument(selfClient);
      }
      if (state.value === "init_tee_connexion") {
        get().initTeeConnection(selfClient);
      }
      if (state.value === "ready_to_prove" && get().userConfirmed) {
        get().startProving(selfClient);
      }
      if (state.value === "proving" && get().circuitType === "register") {
        trackOnboardingStep(selfClient, OnboardingEvents.PROOF_STARTED);
      }
      if (state.value === "post_proving") {
        if (get().circuitType === "register") {
          set({ didNewRegistrationProof: true });
        }
        get().postProving(selfClient);
      }
      if (get().circuitType !== "disclose" && (state.value === "error" || state.value === "failure")) {
        get()._handleRegisterErrorOrFailure(selfClient);
      }
      if (state.value === "completed") {
        if (get().circuitType === "register") {
          (async () => {
            try {
              await markCurrentDocumentAsRegistered(selfClient);
            } catch (error) {
              console.error("Error marking document as registered:", error);
            }
          })();
        }
        if (get().circuitType !== "disclose") {
          get()._handleAccountVerifiedSuccess(selfClient);
        }
        if (get().circuitType === "disclose") {
          selfClient.getSelfAppState().handleProofResult(true);
        }
        if (get().circuitType === "register" && get().didNewRegistrationProof) {
          trackOnboardingStep(selfClient, OnboardingEvents.PROOF_SUCCEEDED);
          completeOnboardingAttempt(selfClient);
        } else if (get().circuitType === "register" && !get().didNewRegistrationProof) {
          recoverOnboardingAttempt(selfClient);
        }
        emitVerificationComplete(true);
        selfClient.navigation?.disableKeychainErrorModal?.();
      }
      if (state.value === "passport_not_supported") {
        get()._handlePassportNotSupported(selfClient);
      }
      if (state.value === "account_recovery_choice") {
        moveOnboardingAttemptToRecovery(selfClient);
        get()._handleAccountRecoveryChoice(selfClient);
      }
      if (state.value === "passport_data_not_found") {
        get()._handlePassportDataNotFound(selfClient);
      }
      if (state.value === "failure") {
        const { error_code, reason } = get();
        if (get().circuitType === "disclose") {
          selfClient.getSelfAppState().handleProofResult(false, error_code ?? void 0, reason ?? void 0);
        } else if (get().circuitType !== null) {
          failOnboardingAttempt(selfClient, "proof_generation_started", reason ?? error_code ?? "proof_failure", {
            recoverable: false,
            proof_type: get().circuitType
          });
        }
        emitVerificationComplete(false, {
          code: error_code ?? "proof_failure",
          message: reason ?? "Proof verification failed"
        });
      }
      if (state.value === "error") {
        if (get().circuitType === "disclose") {
          selfClient.getSelfAppState().handleProofResult(false, "error", "error");
        } else if (get().circuitType !== null) {
          failOnboardingAttempt(selfClient, "proof_generation_started", get().reason ?? get().error_code ?? "error", {
            recoverable: true,
            proof_type: get().circuitType
          });
        }
        emitVerificationComplete(false, {
          code: get().error_code ?? "error",
          message: get().reason ?? "Unexpected proving error"
        });
        selfClient.navigation?.disableKeychainErrorModal?.();
      }
    });
  }
  return {
    currentState: "idle",
    attestation: null,
    serverPublicKey: null,
    sharedKey: null,
    wsConnection: null,
    wsHandlers: null,
    wsReconnectAttempts: 0,
    socketConnection: null,
    uuid: null,
    userConfirmed: false,
    passportData: null,
    secret: null,
    circuitType: null,
    didNewRegistrationProof: false,
    isMock: false,
    env: null,
    error_code: null,
    reason: null,
    endpointType: null,
    cancel: async (selfClient) => {
      const context = createProofContext(selfClient, "cancel");
      selfClient.logProofEvent("warn", "Proving flow cancelled by UI", context);
      let cancellationError = null;
      if (actor) {
        try {
          actor.stop();
        } catch (error) {
          cancellationError = error instanceof Error ? error : new Error(String(error));
        } finally {
          actor = null;
        }
      }
      try {
        get()._closeConnections(selfClient);
      } catch (error) {
        cancellationError ?? (cancellationError = error instanceof Error ? error : new Error(String(error)));
      }
      selfClient.navigation?.disableKeychainErrorModal?.();
      resetProvingState();
      if (cancellationError) {
        throw cancellationError;
      }
    },
    _handleWebSocketMessage: async (event, selfClient) => {
      if (!actor) {
        console.error("Cannot process message: State machine not initialized.");
        return;
      }
      const startTime = Date.now();
      const context = createProofContext(selfClient, "_handleWebSocketMessage");
      try {
        const result = JSON.parse(event.data);
        selfClient.logProofEvent("info", "WebSocket message received", context);
        if (result.result?.attestation) {
          selfClient.logProofEvent("info", "Attestation received", context);
          const attestationData = result.result.attestation;
          set({ attestation: attestationData });
          const attestationToken = Buffer.from(attestationData).toString("utf-8");
          const { userPubkey, serverPubkey, imageHash, verified } = validatePKIToken(
            attestationToken,
            selfClient?.config?.debug ?? false
          );
          const pcr0Mapping = await checkPCR0Mapping(imageHash);
          if (!(selfClient?.config?.debug ?? false) && !pcr0Mapping) {
            console.error("PCR0 mapping not found");
            actor.send({ type: "CONNECT_ERROR" });
            return;
          }
          if (clientPublicKeyHex !== userPubkey.toString("hex")) {
            console.error("User public key does not match");
            actor.send({ type: "CONNECT_ERROR" });
            return;
          }
          if (!verified) {
            selfClient.logProofEvent("error", "Attestation verification failed", context, {
              failure: "PROOF_FAILED_TEE_PROCESSING",
              duration_ms: Date.now() - startTime
            });
            console.error("Attestation verification failed");
            actor.send({ type: "CONNECT_ERROR" });
            return;
          }
          selfClient.logProofEvent("info", "Attestation verified", context);
          const serverKey = ec.keyFromPublic(serverPubkey, "hex");
          const derivedKey = clientKey.derive(serverKey.getPublic());
          set({
            serverPublicKey: serverKey.getPublic(true, "hex"),
            sharedKey: Buffer.from(derivedKey.toArray("be", 32))
          });
          selfClient.logProofEvent("info", "Shared key derived", context);
          actor.send({ type: "CONNECT_SUCCESS" });
        } else if (result.id === 2 && typeof result.result === "string" && !result.error) {
          selfClient.logProofEvent("info", "Hello ACK received", context);
          const statusUuid = result.result;
          if (get().uuid !== statusUuid) {
            selfClient.logProofEvent("warn", "Status UUID mismatch", context, {
              received_uuid: statusUuid
            });
            console.warn(
              `Received status UUID (${statusUuid}) does not match stored UUID (${get().uuid}). Using received UUID.`
            );
          }
          const endpointType = get().endpointType;
          if (!endpointType) {
            selfClient.logProofEvent("error", "Endpoint type missing", context, {
              failure: "PROOF_FAILED_TEE_PROCESSING",
              duration_ms: Date.now() - startTime
            });
            console.error("Cannot start Socket.IO listener: endpointType not set.");
            actor.send({ type: "PROVE_ERROR" });
            return;
          }
          get()._startSocketIOStatusListener(statusUuid, endpointType, selfClient);
        } else if (result.error) {
          selfClient.logProofEvent("error", "TEE returned error", context, {
            failure: "PROOF_FAILED_TEE_PROCESSING",
            error: result.error,
            duration_ms: Date.now() - startTime
          });
          console.error("Received error from TEE:", result.error);
          actor.send({ type: "PROVE_ERROR" });
        } else {
          selfClient.logProofEvent("warn", "Unknown message format", context);
          console.warn("Received unknown message format from TEE:", result);
        }
      } catch (error) {
        selfClient.logProofEvent("error", "WebSocket message handling failed", context, {
          failure: get().currentState === "init_tee_connexion" ? "PROOF_FAILED_CONNECTION" : "PROOF_FAILED_TEE_PROCESSING",
          error: error instanceof Error ? error.message : String(error),
          duration_ms: Date.now() - startTime
        });
        console.error("Error processing WebSocket message:", error);
        if (get().currentState === "init_tee_connexion") {
          actor.send({ type: "CONNECT_ERROR" });
        } else {
          actor.send({ type: "PROVE_ERROR" });
        }
      }
    },
    _handleRegisterErrorOrFailure: async (selfClient) => {
      try {
        const hasValid = await hasAnyValidRegisteredDocument(selfClient);
        selfClient.emit("PROVING_REGISTER_ERROR_OR_FAILURE" /* PROVING_REGISTER_ERROR_OR_FAILURE */, {
          hasValidDocument: hasValid
        });
      } catch {
        selfClient.emit("PROVING_REGISTER_ERROR_OR_FAILURE" /* PROVING_REGISTER_ERROR_OR_FAILURE */, {
          hasValidDocument: false
        });
      }
    },
    _startSocketIOStatusListener: (receivedUuid, endpointType, selfClient) => {
      if (!actor) {
        console.error("Cannot start Socket.IO listener: Actor not available.");
        return;
      }
      const url = getWSDbRelayerUrl(endpointType);
      const socket = socketIo(url, {
        path: "/",
        transports: ["websocket"]
      });
      set({ socketConnection: socket });
      const context = createProofContext(selfClient, "_startSocketIOStatusListener");
      selfClient.logProofEvent("info", "Socket.IO listener started", context, { url });
      let terminalStatusHandled = false;
      socket.on("connect", () => {
        socket?.emit("subscribe", receivedUuid);
        selfClient.logProofEvent("info", "Socket.IO connected", context);
      });
      socket.on("connect_error", (error) => {
        console.error("SocketIO connection error:", error);
        selfClient.logProofEvent("error", "Socket.IO connection error", context, {
          failure: "PROOF_FAILED_CONNECTION",
          error: error instanceof Error ? error.message : String(error)
        });
        actor.send({ type: "PROVE_ERROR" });
        set({ socketConnection: null });
      });
      socket.on("disconnect", (_reason) => {
        const currentActor = actor;
        const currentState = get().currentState;
        selfClient.logProofEvent("warn", "Socket.IO disconnected", context);
        if (terminalStatusHandled) {
          set({ socketConnection: null });
          return;
        }
        if (currentActor && (currentState === "ready_to_prove" || currentState === "listening_for_status")) {
          console.error(`SocketIO disconnected unexpectedly during ${currentState}.`);
          selfClient.logProofEvent("error", "Socket.IO disconnected unexpectedly", context, {
            failure: "PROOF_FAILED_CONNECTION",
            state: currentState
          });
          currentActor.send({ type: "PROVE_ERROR" });
        }
        set({ socketConnection: null });
      });
      socket.on("status", (message) => {
        try {
          const data = parseStatusMessage(message);
          selfClient.logProofEvent("info", "Status message received", context, {
            status: data.status
          });
          const result = handleStatusCode(data, get().circuitType);
          if (result.stateUpdate) {
            set(result.stateUpdate);
          }
          result.analytics?.forEach(({ event, data: eventData }) => {
            if (event === "SOCKETIO_PROOF_FAILURE") {
              selfClient.logProofEvent("error", "TEE processing failed", context, {
                failure: "PROOF_FAILED_TEE_PROCESSING",
                error_code: eventData?.error_code,
                reason: eventData?.reason
              });
            } else if (event === "SOCKETIO_PROOF_SUCCESS") {
              selfClient.logProofEvent("info", "TEE processing succeeded", context);
            }
          });
          if (result.actorEvent) {
            if (result.actorEvent.type === "PROVE_FAILURE") {
              console.error("Proof generation/verification failed (status 3 or 5).");
              console.error(data);
            }
            actor.send(result.actorEvent);
          }
          if (result.shouldDisconnect) {
            terminalStatusHandled = true;
            socket?.disconnect();
          }
        } catch (error) {
          console.error("Error handling status message:", error);
          selfClient.logProofEvent("error", "Status message parsing failed", context, {
            failure: "PROOF_FAILED_MESSAGE_PARSING",
            error: error instanceof Error ? error.message : String(error)
          });
          actor.send({ type: "PROVE_ERROR" });
        }
      });
    },
    _handleWsOpen: (selfClient) => {
      if (!actor) {
        return;
      }
      const ws = get().wsConnection;
      if (!ws) {
        return;
      }
      const connectionUuid = v4();
      const context = createProofContext(selfClient, "_handleWsOpen", {
        sessionId: connectionUuid
      });
      selfClient.logProofEvent("info", "WebSocket open", context);
      set({ uuid: connectionUuid });
      const helloBody = {
        jsonrpc: "2.0",
        method: "openpassport_hello",
        id: 1,
        params: {
          user_pubkey: [...Array.from(Buffer.from(clientPublicKeyHex, "hex"))],
          uuid: connectionUuid
        }
      };
      ws.send(JSON.stringify(helloBody));
      selfClient.logProofEvent("info", "WS hello sent", context);
    },
    _handleWsError: (error, selfClient) => {
      console.error("TEE WebSocket error event:", error);
      if (!actor) {
        return;
      }
      const context = createProofContext(selfClient, "_handleWsError");
      selfClient.logProofEvent("error", "TEE WebSocket error", context, {
        failure: "PROOF_FAILED_CONNECTION",
        error: error instanceof Error ? error.message : String(error)
      });
      get()._handleWebSocketMessage(
        new MessageEvent("error", {
          data: JSON.stringify({ error: "WebSocket connection error" })
        }),
        selfClient
      );
    },
    _handleWsClose: (event, selfClient) => {
      if (!actor) {
        return;
      }
      const context = createProofContext(selfClient, "_handleWsClose");
      selfClient.logProofEvent("warn", "TEE WebSocket closed", context, {
        code: event.code,
        reason: event.reason
      });
      const currentState = get().currentState;
      if (currentState === "init_tee_connexion" || currentState === "proving" || currentState === "listening_for_status") {
        console.error(`TEE WebSocket closed unexpectedly during ${currentState}.`);
        get()._handleWebSocketMessage(
          new MessageEvent("error", {
            data: JSON.stringify({ error: "WebSocket closed unexpectedly" })
          }),
          selfClient
        );
      }
      if (currentState === "ready_to_prove") {
        const MAX_RECONNECT_ATTEMPTS = 3;
        const attempts = get().wsReconnectAttempts;
        if (attempts < MAX_RECONNECT_ATTEMPTS) {
          selfClient.logProofEvent("info", "TEE WebSocket reconnection attempt", context, {
            attempt: attempts + 1,
            max_attempts: MAX_RECONNECT_ATTEMPTS
          });
          set({ wsConnection: null, wsReconnectAttempts: attempts + 1 });
          const backoffMs = Math.min(1e3 * Math.pow(2, attempts), 1e4);
          setTimeout(() => {
            if (get().currentState === "ready_to_prove") {
              get()._reconnectTeeWebSocket(selfClient);
            }
          }, backoffMs);
          return;
        }
        selfClient.logProofEvent("error", "TEE WebSocket reconnection exhausted", context, {
          failure: "PROOF_FAILED_CONNECTION",
          attempts: MAX_RECONNECT_ATTEMPTS
        });
        get()._handleWebSocketMessage(
          new MessageEvent("error", {
            data: JSON.stringify({ error: "WebSocket reconnection failed" })
          }),
          selfClient
        );
      }
      if (get().wsConnection) {
        set({ wsConnection: null });
      }
    },
    /**
     * Re-establishes the TEE WebSocket connection using stored circuit parameters.
     * Called automatically when connection is lost in ready_to_prove state.
     */
    _reconnectTeeWebSocket: async (selfClient) => {
      const context = createProofContext(selfClient, "_reconnectTeeWebSocket");
      const { passportData, circuitType } = get();
      if (!passportData || !circuitType) {
        selfClient.logProofEvent("error", "Reconnect failed: missing prerequisites", context);
        return false;
      }
      const typedCircuitType = circuitType;
      const circuitName = typedCircuitType === "disclose" ? passportData.documentCategory === "aadhaar" ? "disclose_aadhaar" : passportData.documentCategory === "kyc" ? "disclose_kyc" : "disclose" : getCircuitNameFromPassportData(passportData, typedCircuitType);
      const wsRpcUrl = resolveWebSocketUrl(selfClient, typedCircuitType, passportData, circuitName);
      if (!wsRpcUrl) {
        selfClient.logProofEvent("error", "Reconnect failed: no WebSocket URL", context);
        return false;
      }
      selfClient.logProofEvent("info", "TEE WebSocket reconnection started", context);
      return new Promise((resolve) => {
        const ws = new WebSocket(wsRpcUrl);
        const RECONNECT_TIMEOUT_MS = 15e3;
        const wsHandlers = {
          message: (event) => get()._handleWebSocketMessage(event, selfClient),
          open: () => {
            selfClient.logProofEvent("info", "TEE WebSocket reconnected", context);
            set({ wsReconnectAttempts: 0 });
            resolve(true);
          },
          error: (error) => get()._handleWsError(error, selfClient),
          close: (event) => get()._handleWsClose(event, selfClient)
        };
        set({ wsConnection: ws, wsHandlers });
        ws.addEventListener("message", wsHandlers.message);
        ws.addEventListener("open", wsHandlers.open);
        ws.addEventListener("error", wsHandlers.error);
        ws.addEventListener("close", wsHandlers.close);
        setTimeout(() => {
          if (ws.readyState !== WebSocket.OPEN) {
            selfClient.logProofEvent("warn", "TEE WebSocket reconnection timeout", context);
            resolve(false);
          }
        }, RECONNECT_TIMEOUT_MS);
      });
    },
    init: async (selfClient, circuitType, userConfirmed = false) => {
      get()._closeConnections(selfClient);
      selfClient.navigation?.enableKeychainErrorModal?.();
      if (actor) {
        try {
          actor.stop();
        } catch (error) {
          console.error("Error stopping actor:", error);
        }
        actor = null;
      }
      resetProvingState({
        userConfirmed,
        circuitType
      });
      actor = createActor(provingMachine);
      setupActorSubscriptions(actor, selfClient);
      actor.start();
      const selectedDocument = await loadSelectedDocument(selfClient);
      if (!selectedDocument) {
        console.error("No document found for proving");
        console.error("No document found for proving in init");
        actor.send({ type: "PASSPORT_DATA_NOT_FOUND" });
        return;
      }
      const { data: passportData } = selectedDocument;
      const secret = await selfClient.getPrivateKey();
      if (!secret) {
        console.error("Could not load secret");
        actor.send({ type: "ERROR" });
        return;
      }
      const env = passportData.mock ? "stg" : "prod";
      const isMock = passportData.mock === true;
      if (isMock) {
        markCurrentAttemptAsMock(selfClient);
      }
      set({ passportData, secret, env, isMock });
      set({ circuitType });
      const needsDscParsing = passportData.documentCategory === "passport" || passportData.documentCategory === "id_card";
      const hasParsedDsc = needsDscParsing && Boolean(passportData.dsc_parsed?.authorityKeyIdentifier);
      if (circuitType === "dsc" && !needsDscParsing) {
        console.error(`DSC circuit is not supported for ${passportData.documentCategory} documents`);
        actor.send({ type: "ERROR" });
        return;
      }
      const shouldParseDocument = circuitType === "dsc" || needsDscParsing && !hasParsedDsc;
      if (shouldParseDocument) {
        actor.send({ type: "PARSE_ID_DOCUMENT" });
      } else {
        actor.send({ type: "FETCH_DATA" });
      }
    },
    parseIDDocument: async (selfClient) => {
      _checkActorInitialized(actor);
      const startTime = Date.now();
      const context = createProofContext(selfClient, "parseIDDocument");
      selfClient.logProofEvent("info", "Parsing ID document started", context);
      try {
        const { passportData, env } = get();
        if (!passportData) {
          throw new Error("PassportData is not available");
        }
        selfClient.logProofEvent("info", "ID document parsing process started", context);
        const skiPem = await getSKIPEM(env === "stg" ? "staging" : "production");
        const parsedPassportData = initPassportDataParsing(passportData, skiPem);
        if (!parsedPassportData) {
          throw new Error("Failed to parse passport data");
        }
        const passportMetadata = parsedPassportData.passportMetadata;
        trackBranchEvent(selfClient, BiometricEvents.DOCUMENT_PARSED, {
          document_type: passportData.documentCategory === "id_card" ? "id_card" : "passport",
          country_code: passportMetadata.countryCode,
          signature_algorithm: passportMetadata.signatureAlgorithm,
          csca_hash_algorithm: passportMetadata.cscaHashFunction
        });
        console.log("passport data parsed successfully, storing in keychain");
        await storePassportData(selfClient, parsedPassportData);
        console.log("passport data stored in keychain");
        set({ passportData: parsedPassportData });
        selfClient.logProofEvent("info", "ID document parsing succeeded", context, {
          duration_ms: Date.now() - startTime
        });
        actor.send({ type: "PARSE_SUCCESS" });
      } catch (error) {
        selfClient.logProofEvent("error", "ID document parsing failed", context, {
          failure: "PROOF_FAILED_PARSING",
          error: error instanceof Error ? error.message : String(error),
          duration_ms: Date.now() - startTime
        });
        console.error("Error parsing ID document:", error);
        actor.send({ type: "PARSE_ERROR" });
      }
    },
    startFetchingData: async (selfClient) => {
      _checkActorInitialized(actor);
      const startTime = Date.now();
      const context = createProofContext(selfClient, "startFetchingData");
      selfClient.logProofEvent("info", "Fetching DSC data started", context);
      try {
        const { passportData, env } = get();
        if (!passportData) {
          throw new Error("PassportData is not available");
        }
        const document = passportData.documentCategory;
        console.log("document", document);
        switch (passportData.documentCategory) {
          case "passport":
          case "id_card":
            if (!passportData?.dsc_parsed?.authorityKeyIdentifier) {
              const docType = passportData.documentCategory;
              selfClient.logProofEvent("error", `Missing parsed DSC in ${docType} data`, context, {
                failure: "PROOF_FAILED_DATA_FETCH",
                duration_ms: Date.now() - startTime
              });
              console.error(`Missing parsed DSC in ${docType} data`);
              actor.send({ type: "FETCH_ERROR" });
              return;
            }
            selfClient.logProofEvent("info", "Protocol store fetch", context, {
              step: "protocol_store_fetch",
              document
            });
            await fetchAllTreesAndCircuits(selfClient, document, env, passportData.dsc_parsed.authorityKeyIdentifier);
            break;
          case "aadhaar":
            selfClient.logProofEvent("info", "Protocol store fetch", context, {
              step: "protocol_store_fetch",
              document
            });
            await selfClient.getProtocolState().aadhaar.fetch_all(env);
            break;
          case "kyc":
            selfClient.logProofEvent("info", "Protocol store fetch", context, {
              step: "protocol_store_fetch",
              document
            });
            await selfClient.getProtocolState().kyc.fetch_all(env);
            break;
        }
        selfClient.logProofEvent("info", "Data fetch succeeded", context, {
          duration_ms: Date.now() - startTime
        });
        actor.send({ type: "FETCH_SUCCESS" });
      } catch (error) {
        selfClient.logProofEvent("error", "Data fetch failed", context, {
          failure: "PROOF_FAILED_DATA_FETCH",
          error: error instanceof Error ? error.message : String(error),
          duration_ms: Date.now() - startTime
        });
        console.error("Error fetching data:", error);
        actor.send({ type: "FETCH_ERROR" });
      }
    },
    validatingDocument: async (selfClient) => {
      _checkActorInitialized(actor);
      const startTime = Date.now();
      const context = createProofContext(selfClient, "validatingDocument");
      selfClient.logProofEvent("info", "Validating document started", context);
      try {
        const { passportData, secret, circuitType } = get();
        if (!passportData) {
          throw new Error("PassportData is not available");
        }
        const isSupported = await checkDocumentSupported(passportData, {
          getDeployedCircuits: (documentCategory) => selfClient.getProtocolState()[documentCategory].deployed_circuits
        });
        selfClient.logProofEvent("info", "Document support check", context, {
          supported: isSupported.status === "passport_supported",
          duration_ms: Date.now() - startTime
        });
        if (isSupported.status !== "passport_supported") {
          selfClient.logProofEvent("error", "Passport not supported", context, {
            failure: "PROOF_FAILED_VALIDATION",
            details: isSupported.details,
            duration_ms: Date.now() - startTime
          });
          console.error("Passport not supported:", isSupported.status, isSupported.details);
          if (passportData.documentCategory === "passport" || passportData.documentCategory === "id_card") {
            const biometricData = passportData;
            trackBranchEvent(selfClient, BiometricEvents.DOCUMENT_UNSUPPORTED, {
              document_type: passportData.documentCategory,
              country_code: biometricData.passportMetadata?.countryCode,
              signature_algorithm: biometricData.passportMetadata?.signatureAlgorithm,
              unsupported_reason: isSupported.status === "csca_not_found" || isSupported.status === "passport_metadata_missing" ? "unknown_dsc" : isSupported.status === "registration_circuit_not_supported" || isSupported.status === "dsc_circuit_not_supported" ? "unsupported_algo" : "country_not_in_list"
            });
          }
          await clearPassportData(selfClient);
          actor.send({ type: "PASSPORT_NOT_SUPPORTED" });
          return;
        }
        if (circuitType === "disclose") {
          const isRegisteredWithLocalCSCA = await isUserRegistered(
            passportData,
            secret,
            (documentCategory) => getCommitmentTree(selfClient, documentCategory)
          );
          selfClient.logProofEvent("info", "Local CSCA registration check", context, {
            registered: isRegisteredWithLocalCSCA
          });
          if (isRegisteredWithLocalCSCA) {
            selfClient.logProofEvent("info", "Validation succeeded", context, {
              duration_ms: Date.now() - startTime
            });
            actor.send({ type: "VALIDATION_SUCCESS" });
            return;
          } else {
            selfClient.logProofEvent("error", "Passport data not found", context, {
              failure: "PROOF_FAILED_VALIDATION",
              duration_ms: Date.now() - startTime
            });
            actor.send({ type: "PASSPORT_DATA_NOT_FOUND" });
            return;
          }
        } else {
          const bypassDocumentRegistrationCheck = selfClient.config?.devConfig?.shouldBypassDocumentRegistrationCheck?.() ?? false;
          if (bypassDocumentRegistrationCheck) {
            selfClient.logProofEvent(
              "warn",
              "Dev bypass active: skipping document registration / nullifier checks",
              context
            );
          }
          const { isRegistered, csca } = bypassDocumentRegistrationCheck ? { isRegistered: false, csca: void 0 } : await isUserRegisteredWithAlternativeCSCA(passportData, secret, {
            getCommitmentTree: (docCategory) => getCommitmentTree(selfClient, docCategory),
            getAltCSCA: (docType) => {
              if (docType === "aadhaar" || docType === "kyc") {
                const publicKeys = selfClient.getProtocolState()[docType].public_keys;
                return publicKeys ? Object.fromEntries(publicKeys.map((key) => [key, key])) : {};
              }
              return selfClient.getProtocolState()[docType].alternative_csca;
            }
          });
          selfClient.logProofEvent("info", "Alternative CSCA registration check", context, {
            registered: isRegistered
          });
          if (isRegistered) {
            await reStorePassportDataWithRightCSCA(selfClient, passportData, csca);
            (async () => {
              try {
                await markCurrentDocumentAsRegistered(selfClient);
              } catch (error) {
                console.error("Error marking document as registered:", error);
              }
            })();
            set({ circuitType: "register" });
            selfClient.logProofEvent("info", "Document already registered", context, {
              duration_ms: Date.now() - startTime
            });
            actor.send({ type: "ALREADY_REGISTERED" });
            return;
          }
          const isNullifierOnchain = bypassDocumentRegistrationCheck ? false : await isDocumentNullified(passportData);
          selfClient.logProofEvent("info", "Nullifier check", context, {
            nullified: isNullifierOnchain
          });
          if (isNullifierOnchain) {
            selfClient.logProofEvent("error", "Passport nullified", context, {
              failure: "PROOF_FAILED_VALIDATION",
              duration_ms: Date.now() - startTime
            });
            console.warn(
              "Passport is nullified, but not registered with this secret. Navigating to AccountRecoveryChoice"
            );
            actor.send({ type: "ACCOUNT_RECOVERY_CHOICE" });
            return;
          }
          const document = passportData.documentCategory;
          if (document === "passport" || document === "id_card") {
            const bypassDscRegistrationCheck = selfClient.config?.devConfig?.shouldBypassDscRegistrationCheck?.() ?? false;
            if (bypassDscRegistrationCheck) {
              selfClient.logProofEvent("warn", "Dev bypass active: skipping DSC tree check", context);
            }
            const isDscRegistered = bypassDscRegistrationCheck ? false : await checkIfPassportDscIsInTree(passportData, selfClient.getProtocolState()[document].dsc_tree);
            selfClient.logProofEvent("info", "DSC tree check", context, {
              dsc_registered: isDscRegistered
            });
            if (isDscRegistered) {
              set({ circuitType: "register" });
            }
          }
          selfClient.logProofEvent("info", "Validation succeeded", context, {
            duration_ms: Date.now() - startTime
          });
          actor.send({ type: "VALIDATION_SUCCESS" });
        }
      } catch (error) {
        selfClient.logProofEvent("error", "Validation failed", context, {
          failure: "PROOF_FAILED_VALIDATION",
          error: error instanceof Error ? error.message : String(error),
          duration_ms: Date.now() - startTime
        });
        console.error("Error validating passport:", error);
        actor.send({ type: "VALIDATION_ERROR" });
      }
    },
    initTeeConnection: async (selfClient) => {
      const startTime = Date.now();
      const baseContext = createProofContext(selfClient, "initTeeConnection");
      const { passportData } = get();
      if (!passportData) {
        selfClient.logProofEvent("error", "Passport data missing", baseContext, {
          failure: "PROOF_FAILED_CONNECTION",
          duration_ms: Date.now() - startTime
        });
        throw new Error("PassportData is not available");
      }
      const circuitType = get().circuitType;
      let circuitName;
      if (circuitType === "disclose") {
        circuitName = passportData.documentCategory === "aadhaar" ? "disclose_aadhaar" : passportData.documentCategory === "kyc" ? "disclose_kyc" : "disclose";
      } else {
        circuitName = getCircuitNameFromPassportData(passportData, circuitType);
      }
      const wsRpcUrl = resolveWebSocketUrl(selfClient, circuitType, passportData, circuitName);
      selfClient.logProofEvent("info", "Circuit resolution", baseContext, {
        circuit_name: circuitName,
        ws_url: wsRpcUrl
      });
      if (!circuitName) {
        actor?.send({ type: "CONNECT_ERROR" });
        selfClient.logProofEvent("error", "Circuit name missing", baseContext, {
          failure: "PROOF_FAILED_CONNECTION",
          duration_ms: Date.now() - startTime
        });
        throw new Error("Could not determine circuit name");
      }
      if (!wsRpcUrl) {
        actor?.send({ type: "CONNECT_ERROR" });
        selfClient.logProofEvent("error", "WebSocket URL missing", baseContext, {
          failure: "PROOF_FAILED_CONNECTION",
          duration_ms: Date.now() - startTime
        });
        throw new Error("No WebSocket URL available for TEE connection");
      }
      get()._closeConnections(selfClient);
      selfClient.logProofEvent("info", "TEE connection attempt", baseContext);
      return new Promise((resolve) => {
        const ws = new WebSocket(wsRpcUrl);
        const handleConnectSuccess = () => {
          selfClient.logProofEvent("info", "TEE connection succeeded", baseContext, {
            duration_ms: Date.now() - startTime
          });
          resolve(true);
        };
        const handleConnectError = (msg = "connect_error") => {
          selfClient.logProofEvent("error", "TEE connection failed", baseContext, {
            failure: "PROOF_FAILED_CONNECTION",
            error: msg,
            duration_ms: Date.now() - startTime
          });
          resolve(false);
        };
        const wsHandlers = {
          message: (event) => get()._handleWebSocketMessage(event, selfClient),
          open: () => get()._handleWsOpen(selfClient),
          error: (error) => get()._handleWsError(error, selfClient),
          close: (event) => get()._handleWsClose(event, selfClient)
        };
        set({ wsConnection: ws, wsHandlers, wsReconnectAttempts: 0 });
        ws.addEventListener("message", wsHandlers.message);
        ws.addEventListener("open", wsHandlers.open);
        ws.addEventListener("error", wsHandlers.error);
        ws.addEventListener("close", wsHandlers.close);
        if (!actor) {
          return;
        }
        const unsubscribe = actor.subscribe((state) => {
          if (state.matches("ready_to_prove")) {
            handleConnectSuccess();
            unsubscribe.unsubscribe();
          } else if (state.matches("error")) {
            handleConnectError();
            unsubscribe.unsubscribe();
          }
        });
      });
    },
    startProving: async (selfClient) => {
      _checkActorInitialized(actor);
      const startTime = Date.now();
      let { wsConnection } = get();
      const { sharedKey, passportData, secret, uuid: uuid2 } = get();
      const context = createProofContext(selfClient, "startProving", {
        sessionId: uuid2 || get().uuid || "unknown-session"
      });
      if (get().currentState !== "ready_to_prove") {
        selfClient.logProofEvent("error", "Not in ready_to_prove state", context, {
          failure: "PROOF_FAILED_CONNECTION"
        });
        console.error("Cannot start proving: Not in ready_to_prove state.");
        return;
      }
      if (!sharedKey || !passportData || !secret || !uuid2) {
        selfClient.logProofEvent("error", "Missing proving prerequisites", context, {
          failure: "PROOF_FAILED_CONNECTION"
        });
        console.error("Cannot start proving: Missing sharedKey, passportData, secret, or uuid.");
        actor.send({ type: "PROVE_ERROR" });
        return;
      }
      if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
        selfClient.logProofEvent("warn", "WebSocket not ready, attempting reconnection", context, {
          wsConnectionExists: !!wsConnection,
          readyState: wsConnection?.readyState
        });
        const reconnected = await get()._reconnectTeeWebSocket(selfClient);
        if (!reconnected) {
          selfClient.logProofEvent("error", "WebSocket reconnection failed", context, {
            failure: "PROOF_FAILED_CONNECTION"
          });
          actor.send({ type: "PROVE_ERROR" });
          return;
        }
        wsConnection = get().wsConnection;
        if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
          selfClient.logProofEvent("error", "Reconnected WebSocket not ready", context, {
            failure: "PROOF_FAILED_CONNECTION"
          });
          actor.send({ type: "PROVE_ERROR" });
          return;
        }
      }
      try {
        selfClient.emit("PROVING_BEGIN_GENERATION" /* PROVING_BEGIN_GENERATION */, {
          uuid: uuid2,
          isMock: passportData?.mock ?? false,
          context
        });
        selfClient.logProofEvent("info", "Payload generation started", context);
        const submitBody = await get()._generatePayload(selfClient);
        const activeWsConnection = get().wsConnection;
        if (!activeWsConnection) {
          throw new Error("WebSocket connection lost during payload generation");
        }
        activeWsConnection.send(JSON.stringify(submitBody));
        selfClient.logProofEvent("info", "Payload sent over WebSocket", context);
        actor.send({ type: "START_PROVING" });
        selfClient.logProofEvent("info", "Proving started", context, {
          duration_ms: Date.now() - startTime
        });
      } catch (error) {
        selfClient.logProofEvent("error", "startProving failed", context, {
          failure: "PROOF_FAILED_PAYLOAD_GEN",
          error: error instanceof Error ? error.message : String(error),
          duration_ms: Date.now() - startTime
        });
        console.error("Error during startProving preparation/send:", error);
        actor.send({ type: "PROVE_ERROR" });
      }
    },
    setUserConfirmed: (selfClient) => {
      set({ userConfirmed: true });
      if (get().currentState === "ready_to_prove") {
        get().startProving(selfClient);
      }
    },
    postProving: (selfClient) => {
      _checkActorInitialized(actor);
      const { circuitType } = get();
      if (circuitType === "dsc") {
        setTimeout(() => {
          get().init(selfClient, "register", true);
        }, 1500);
      } else if (circuitType === "register") {
        actor.send({ type: "COMPLETED" });
      } else if (circuitType === "disclose") {
        actor.send({ type: "COMPLETED" });
      }
    },
    _closeConnections: (_selfClient) => {
      const { wsConnection: ws, wsHandlers } = get();
      if (ws && wsHandlers) {
        try {
          ws.removeEventListener("message", wsHandlers.message);
          ws.removeEventListener("open", wsHandlers.open);
          ws.removeEventListener("error", wsHandlers.error);
          ws.removeEventListener("close", wsHandlers.close);
          ws.close();
        } catch (error) {
          console.error("Error removing listeners or closing WebSocket:", error);
        }
        set({ wsConnection: null, wsHandlers: null });
      }
      const socket = get().socketConnection;
      if (socket) {
        socket.close();
        set({ socketConnection: null });
      }
      set({
        attestation: null,
        serverPublicKey: null,
        sharedKey: null,
        uuid: null,
        endpointType: null
      });
    },
    _generatePayload: async (selfClient) => {
      const startTime = Date.now();
      const { circuitType, passportData, secret, uuid: uuid2, sharedKey, env } = get();
      const context = createProofContext(selfClient, "_generatePayload", {
        sessionId: uuid2 || get().uuid || "unknown-session",
        circuitType: circuitType || null
      });
      selfClient.logProofEvent("info", "Payload generation started", context);
      try {
        if (!passportData) {
          throw new Error("PassportData is not available");
        }
        if (!env) {
          throw new Error("Environment not set");
        }
        if (!sharedKey) {
          throw new Error("Shared key not available");
        }
        const { inputs, circuitName, endpointType, endpoint, circuitTypeWithDocumentExtension } = await _generateCircuitInputs(
          selfClient,
          circuitType,
          secret,
          passportData,
          env,
          selfClient.getSelfAppState().selfApp
        );
        selfClient.logProofEvent("info", "Inputs generated", context, {
          circuit_name: circuitName,
          endpoint_type: endpointType
        });
        const selfApp = selfClient.getSelfAppState().selfApp;
        const userDefinedData = getSolidityPackedUserContextData(
          selfApp?.chainID ?? 0,
          selfApp?.userId ?? "",
          selfApp?.userDefinedData ?? ""
        ).slice(2);
        const payload = getPayload(
          inputs,
          circuitTypeWithDocumentExtension,
          circuitName,
          endpointType,
          endpoint,
          selfApp?.version,
          userDefinedData,
          selfApp?.selfDefinedData ?? ""
        );
        const payloadSize = JSON.stringify(payload).length;
        const encryptedPayload = _encryptPayload(payload, sharedKey);
        selfClient.logProofEvent("info", "Payload encrypted", context, {
          payload_size: payloadSize
        });
        set({ endpointType });
        selfClient.logProofEvent("info", "Payload generation completed", context, {
          duration_ms: Date.now() - startTime
        });
        return _buildSubmitRequest(uuid2, encryptedPayload);
      } catch (error) {
        selfClient.logProofEvent("error", "Payload generation failed", context, {
          failure: "PROOF_FAILED_PAYLOAD_GEN",
          error: error instanceof Error ? error.message : String(error),
          duration_ms: Date.now() - startTime
        });
        throw error;
      }
    },
    _handlePassportNotSupported: (selfClient) => {
      const passportData = get().passportData;
      const countryCode = passportData?.documentCategory !== "aadhaar" ? passportData?.passportMetadata?.countryCode : "IND";
      const documentCategory = passportData?.documentCategory;
      selfClient.emit("PROVING_PASSPORT_NOT_SUPPORTED" /* PROVING_PASSPORT_NOT_SUPPORTED */, {
        countryCode: countryCode ?? null,
        documentCategory: documentCategory ?? null
      });
    },
    _handleAccountRecoveryChoice: (selfClient) => {
      selfClient.emit("PROVING_ACCOUNT_RECOVERY_REQUIRED" /* PROVING_ACCOUNT_RECOVERY_REQUIRED */);
    },
    _handleAccountVerifiedSuccess: (selfClient) => {
      selfClient.emit("PROVING_ACCOUNT_VERIFIED_SUCCESS" /* PROVING_ACCOUNT_VERIFIED_SUCCESS */);
    },
    _handlePassportDataNotFound: (selfClient) => {
      selfClient.emit("PROVING_PASSPORT_DATA_NOT_FOUND" /* PROVING_PASSPORT_DATA_NOT_FOUND */);
    }
  };
});
var createProofContext = (selfClient, stage, overrides = {}) => {
  const selfApp = selfClient.getSelfAppState().selfApp;
  const provingState = selfClient.getProvingState();
  return {
    sessionId: provingState.uuid || "unknown-session",
    userId: selfApp?.userId,
    circuitType: provingState.circuitType || null,
    currentState: provingState.currentState || "unknown-state",
    stage,
    platform: getPlatform(selfClient),
    ...overrides
  };
};
function _checkActorInitialized(actor) {
  if (!actor) {
    throw new Error("State machine not initialized. Call init() first.");
  }
}

// src/stores/mrzStore.tsx
import { create as create2 } from "zustand";
var initialState = {
  passportNumber: "",
  dateOfBirth: "",
  dateOfExpiry: "",
  countryCode: "",
  documentType: ""
};
var useMRZStore = create2((set, get) => ({
  ...initialState,
  setMRZForNFC: (data) => {
    set({
      passportNumber: data.passportNumber,
      dateOfBirth: data.dateOfBirth,
      dateOfExpiry: data.dateOfExpiry,
      countryCode: data.countryCode,
      documentType: data.documentType
    });
  },
  clearMRZ: () => {
    set(initialState);
  },
  getMRZ: () => {
    const state = get();
    return {
      documentNumber: state.passportNumber,
      dateOfBirth: state.dateOfBirth,
      dateOfExpiry: state.dateOfExpiry
    };
  },
  update: (patch) => {
    set((state) => ({ ...state, ...patch }));
  }
}));

// src/stores/selfAppStore.tsx
import socketIo2 from "socket.io-client";
import { create as create3 } from "zustand";
import { WS_DB_RELAYER } from "@selfxyz/common";
var useSelfAppStore = create3((set, get) => ({
  selfApp: null,
  sessionId: null,
  socket: null,
  _initSocket: (sessionId, relayUrl) => {
    const connectionUrl = relayUrl.replace(/^https/, "wss").replace(/^http/, "ws");
    const socketUrl = `${connectionUrl}/websocket`;
    const socket = socketIo2(socketUrl, {
      path: "/",
      transports: ["websocket"],
      forceNew: true,
      // Ensure a new connection is established
      query: {
        sessionId,
        clientType: "mobile"
      }
    });
    return socket;
  },
  setSelfApp: (selfApp) => {
    set({ selfApp });
  },
  startAppListener: (sessionId, relayUrl) => {
    const currentSocket = get().socket;
    if (currentSocket && get().sessionId !== sessionId) {
      currentSocket.disconnect();
      set({ socket: null, sessionId: null, selfApp: null });
    } else if (currentSocket && get().sessionId === sessionId) {
      return;
    }
    try {
      const resolvedRelayUrl = relayUrl === void 0 ? WS_DB_RELAYER : relayUrl;
      if (!resolvedRelayUrl) {
        set({ socket: null, sessionId, selfApp: null });
        return;
      }
      const socket = get()._initSocket(sessionId, resolvedRelayUrl);
      set({ socket, sessionId });
      socket.on("connect", () => {
      });
      socket.once("self_app", (data) => {
        try {
          const appData = typeof data === "string" ? JSON.parse(data) : data;
          if (!appData || typeof appData !== "object" || !appData.sessionId) {
            console.error("[SelfAppStore] Invalid app data received");
            set({ selfApp: null });
            return;
          }
          if (appData.sessionId !== get().sessionId) {
            console.warn("[SelfAppStore] Session mismatch, ignoring payload");
            return;
          }
          set({ selfApp: appData });
        } catch (error) {
          console.error("[SelfAppStore] Error processing app data:", error);
          set({ selfApp: null });
        }
      });
      socket.on("connect_error", (error) => {
        console.error("[SelfAppStore] Connection error:", error.message);
      });
      socket.on("error", (error) => {
        console.error("[SelfAppStore] Socket error:", error);
      });
      socket.on("disconnect", (reason) => {
        if (get().socket !== socket) return;
        if (reason === "io server disconnect" || reason === "io client disconnect") {
          set({ socket: null, sessionId: null });
        }
      });
    } catch (error) {
      console.error("[SelfAppStore] Exception in startAppListener:", error);
      get().cleanSelfApp();
    }
  },
  cleanSelfApp: () => {
    const socket = get().socket;
    if (socket) {
      socket.disconnect();
    }
    set({ selfApp: null, sessionId: null, socket: null });
  },
  handleProofResult: (proof_verified, error_code, reason) => {
    const socket = get().socket;
    const sessionId = get().sessionId;
    if (!socket) {
      if (!sessionId) {
        console.error("[SelfAppStore] Cannot handleProofResult: Socket or SessionId missing.");
      }
      return;
    }
    if (!sessionId) {
      console.error("[SelfAppStore] Cannot handleProofResult: Socket or SessionId missing.");
      return;
    }
    if (proof_verified) {
      socket.emit("proof_verified", {
        session_id: sessionId
      });
    } else {
      socket.emit("proof_generation_failed", {
        session_id: sessionId,
        error_code,
        reason
      });
    }
  }
}));

// src/client.ts
var optionalDefaults = {
  clock: {
    now: () => Date.now(),
    sleep: async (ms) => {
      await new Promise((r) => setTimeout(r, ms));
    }
  },
  logger: {
    log: (...args) => console.log(...args)
  }
};
var REQUIRED_ADAPTERS = ["auth", "scanner", "network", "crypto", "documents", "navigation"];
var createListenersMap = () => {
  const map = /* @__PURE__ */ new Map();
  const addListener = (event, cb) => {
    const set = map.get(event) ?? /* @__PURE__ */ new Set();
    set.add(cb);
    map.set(event, set);
  };
  return { map, addListener };
};
function createSelfClient({
  config,
  adapters,
  listeners
}) {
  const cfg = mergeConfig(defaultConfig, config);
  for (const name of REQUIRED_ADAPTERS) {
    if (!(name in adapters) || !adapters[name]) throw notImplemented(name);
  }
  const _adapters = { ...optionalDefaults, ...adapters };
  const _listeners = /* @__PURE__ */ new Map();
  function on(event, cb) {
    const set = _listeners.get(event) ?? /* @__PURE__ */ new Set();
    set.add(cb);
    _listeners.set(event, set);
    return () => set.delete(cb);
  }
  function emit(event, payload) {
    const set = _listeners.get(event);
    if (!set) return;
    for (const cb of Array.from(set)) {
      try {
        cb(payload);
      } catch (err) {
        _adapters.logger.log("error", `event-listener error for event '${event}'`, { event, error: err });
      }
    }
  }
  for (const [event, set] of listeners ?? []) {
    for (const cb of Array.from(set)) {
      on(event, cb);
    }
  }
  async function scanNFC(opts) {
    if (!opts.signal && cfg.timeouts.scanMs) {
      const controller = new AbortController();
      setTimeout(() => controller.abort(), cfg.timeouts.scanMs);
      return _adapters.scanner.scan({ ...opts, signal: controller.signal });
    }
    return _adapters.scanner.scan(opts);
  }
  function trackEvent(event, payload) {
    if (!_adapters.analytics) {
      return;
    }
    _adapters.analytics.trackEvent?.(event, payload);
  }
  function trackNfcEvent(name, properties) {
    if (!_adapters.analytics) {
      return;
    }
    _adapters.analytics.trackNfcEvent?.(name, properties);
  }
  function logNFCEvent(level, message, context, details) {
    if (!_adapters.analytics) {
      return;
    }
    _adapters.analytics.logNFCEvent?.(level, message, context, details);
  }
  async function getPrivateKey() {
    return adapters.auth.getPrivateKey();
  }
  async function hasPrivateKey() {
    if (!adapters.auth) return false;
    try {
      const key = await adapters.auth.getPrivateKey();
      return !!key;
    } catch {
      return false;
    }
  }
  return {
    scanNFC,
    // Analytics
    trackEvent,
    trackNfcEvent,
    logNFCEvent,
    getPrivateKey,
    hasPrivateKey,
    extractMRZInfo,
    on,
    emit,
    logProofEvent: (level, message, context, details) => {
      emit("PROOF_EVENT" /* PROOF_EVENT */, { context, event: message, details, level });
    },
    // TODO: inline for now
    loadDocumentCatalog: async () => {
      return _adapters.documents.loadDocumentCatalog();
    },
    loadDocumentById: async (id) => {
      return _adapters.documents.loadDocumentById(id);
    },
    saveDocumentCatalog: async (catalog) => {
      return _adapters.documents.saveDocumentCatalog(catalog);
    },
    deleteDocument: async (id) => {
      return _adapters.documents.deleteDocument(id);
    },
    saveDocument: async (id, passportData) => {
      return _adapters.documents.saveDocument(id, passportData);
    },
    // for direct, one off access
    getProvingState: () => {
      return useProvingStore.getState();
    },
    getSelfAppState: () => {
      return useSelfAppStore.getState();
    },
    getProtocolState: () => {
      return useProtocolStore.getState();
    },
    getMRZState: () => {
      return useMRZStore.getState();
    },
    goBack: () => {
      adapters.navigation.goBack();
    },
    goTo: (routeName, params) => {
      adapters.navigation.goTo(routeName, params);
    },
    navigation: adapters.navigation,
    network: adapters.network,
    // for reactivity (if needed)
    useProvingStore,
    useSelfAppStore,
    useProtocolStore,
    useMRZStore,
    // Expose config for internal SDK use
    config: cfg
  };
}

// src/context.tsx
import { createContext, useContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
var SelfClientContext = createContext(null);
function SelfClientProvider({
  config,
  adapters,
  listeners,
  children
}) {
  const client = useMemo(() => createSelfClient({ config, adapters, listeners }), [config, adapters, listeners]);
  return /* @__PURE__ */ jsx(SelfClientContext.Provider, { value: client, children });
}
function useSelfClient() {
  const client = useContext(SelfClientContext);
  if (!client) throw new Error("useSelfClient must be used within a SelfClientProvider");
  return client;
}

export {
  defaultConfig,
  mergeConfig,
  SdkError,
  notImplemented,
  sdkError,
  InitError,
  LivenessError,
  MrzParseError,
  NfcParseError,
  SCANNER_ERROR_CODES,
  checkScannedInfo,
  extractMRZInfo,
  extractNameFromMRZ,
  formatDateToYYMMDD,
  parseMRZBirthDate,
  parseMRZExpiryDate,
  completeOnboardingAttempt,
  recoverOnboardingAttempt,
  moveOnboardingAttemptToRecovery,
  failOnboardingAttempt,
  resolveOnboardingBranch,
  setOnboardingBranch,
  trackBranchEvent,
  trackKycVerdict,
  incrementAttemptRetryCount,
  trackOnboardingRetry,
  trackOnboardingStep,
  clearPassportData,
  extractNameFromDocument,
  getAllDocuments,
  hasAnyValidRegisteredDocument,
  loadSelectedDocument,
  markCurrentDocumentAsRegistered,
  reStorePassportDataWithRightCSCA,
  storePassportData,
  updateDocumentRegistrationState,
  SdkEvents,
  getPostVerificationRoute,
  useProvingStore,
  useMRZStore,
  createListenersMap,
  createSelfClient,
  SelfClientContext,
  SelfClientProvider,
  useSelfClient
};
//# sourceMappingURL=chunk-3A3XD7R6.js.map